import React, { useState } from 'react';
import PieChart from './PieChart';

const CoverageTable = ({ people, capabilities, coverageCalculator, onPersonDelete }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredPeople = people.filter(person =>
    person.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Removed unused color functions - now handled by PieChart component

  if (!people.length || !capabilities.length) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Coverage Table</h2>
        <p className="text-gray-500">No data available. Please add people and capabilities in the Editor.</p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900">Capability Coverage Table</h2>
          <div className="flex items-center space-x-4">
            <input
              type="text"
              placeholder="Search people..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1 text-sm"
            />
          </div>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Person
              </th>
              {capabilities.map((capability, index) => (
                <th
                  key={index}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-32"
                  title={capability.description}
                >
                  {capability.name}
                </th>
              ))}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredPeople.map((person, personIndex) => (
              <tr key={personIndex}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {person.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {person.skills?.length || 0} skills
                    </div>
                  </div>
                </td>
                {capabilities.map((capability, capIndex) => {
                  const coverage = coverageCalculator.calculateCoverage(person, capability);
                  const missingSkills = coverageCalculator.getMissingSkills(person, capability);
                  const matchingSkills = coverageCalculator.getMatchingSkills(person, capability);
                  
                  return (
                    <td key={capIndex} className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <PieChart percentage={coverage} size={40} strokeWidth={4} />
                        <div className="flex-1">
                          {missingSkills.length > 0 && (
                            <div
                              className="text-xs text-red-600 cursor-help"
                              title={`Missing: ${missingSkills.join(', ')}`}
                            >
                              Missing {missingSkills.length} skill{missingSkills.length !== 1 ? 's' : ''}
                            </div>
                          )}
                          {matchingSkills.length > 0 && (
                            <div
                              className="text-xs text-green-600 cursor-help"
                              title={`Has: ${matchingSkills.join(', ')}`}
                            >
                              Has {matchingSkills.length} skill{matchingSkills.length !== 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  );
                })}
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => onPersonDelete && onPersonDelete(person.name)}
                    className="text-red-600 hover:text-red-800 text-sm font-medium"
                    title="Delete person"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {capabilities.length > 0 && (
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <h3 className="text-sm font-medium text-gray-900 mb-2">Team Coverage Summary</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4 w-full">
            {capabilities.map((capability, index) => {
              const teamCoverage = coverageCalculator.getTeamCoverage(people, capability);
              return (
                <div key={index} className="bg-white p-3 rounded border">
                  <div className="text-sm font-medium text-gray-900 mb-2">{capability.name}</div>
                  <div className="flex items-center space-x-2">
                    <PieChart percentage={teamCoverage} size={32} strokeWidth={3} />
                    <div className="text-sm font-medium text-gray-600">Team Coverage</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CoverageTable;